import React from "react";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/components/ModalContainer";
import { useTranslation } from "react-i18next";
import { CheckIcon } from "@/components/Icons";

type ExportType = "txt" | "docx" | "pdf" | "srt";

const FORMAT_OPTIONS: { type: ExportType; icon: string }[] = [
  { type: "txt", icon: "📄" },
  { type: "docx", icon: "📝" },
  { type: "pdf", icon: "📕" },
  { type: "srt", icon: "🎬" },
];

const AlertExportTranscriptOptions = () => {
  const alertExportTranscriptOptions = useUISlice(
    (state) => state.alertExportTranscriptOptions
  );
  const [exportType, setExportType] = React.useState<ExportType>("docx");
  const [withTimestamps, setWithTimestamps] = React.useState(false);
  const [withSpeakers, setWithSpeakers] = React.useState(false);
  const [withMonologue, setWithMonologue] = React.useState(false);
  const { t } = useTranslation();

  if (!alertExportTranscriptOptions) return null;

  const _onDismiss = () => {
    setWithTimestamps(false);
    setWithSpeakers(false);
    setWithMonologue(false);
    useUISlice.getState().setAlertExportTranscriptOptions(undefined);
  };

  const handleExport = () => {
    alertExportTranscriptOptions.onExport({
      exportType,
      withTimestamps,
      withSpeakers,
      withMonologue,
    });
    _onDismiss();
  };

  return (
    <ModalContainer
      title={t("alertExportTranscriptOptions.title")}
      onDismiss={_onDismiss}
      hideCancel
      modalWidth={400}
    >
      {/* Format grid */}
      <div className="grid grid-cols-4 gap-3 mb-8">
        {FORMAT_OPTIONS.map(({ type, icon }) => {
          const isSelected = exportType === type;
          return (
            <button
              key={type}
              data-action-id="export.format.select"
              onClick={() => {
                setExportType(type);
                if (type === "srt") {
                  setWithTimestamps(true);
                  setWithMonologue(false);
                }
              }}
              className={`
                relative flex flex-col items-center gap-2.5 py-5 rounded-3xl
                transition-all duration-300 active:scale-95
                ${
                  isSelected
                    ? "bg-[var(--accent)] shadow-[0_8px_24px_-6px_var(--accent-glow)]"
                    : "bg-[var(--fill-1)] hover:bg-[var(--fill-2)]"
                }
              `}
            >
              <span className="text-2xl">{icon}</span>
              <span
                className={`text-sm font-semibold uppercase tracking-wide ${
                  isSelected
                    ? "text-[var(--text-inverse)]"
                    : "text-[var(--text-primary)]"
                }`}
              >
                .{type}
              </span>
            </button>
          );
        })}
      </div>

      {/* Options */}
      <div className="space-y-2 mb-8">
        <ToggleOption
          actionId="export.timestamps.toggle"
          label={t("alertExportTranscriptOptions.includeTimestamps")}
          value={withTimestamps}
          onChange={(v) => {
            setWithTimestamps(v);
            if (v) setWithMonologue(false);
          }}
        />

        {alertExportTranscriptOptions.hasSpeakers && (
          <ToggleOption
            actionId="export.speakers.toggle"
            label={t("alertExportTranscriptOptions.includeSpeakers")}
            value={withSpeakers}
            onChange={(v) => {
              setWithSpeakers(v);
              if (v) setWithMonologue(false);
            }}
          />
        )}

        <ToggleOption
          actionId="export.monologue.toggle"
          label={t("alertExportTranscriptOptions.exportAsMonologue")}
          value={withMonologue}
          onChange={(v) => {
            setWithMonologue(v);
            if (v) {
              setWithSpeakers(false);
              setWithTimestamps(false);
            }
          }}
        />
      </div>

      {/* Export button */}
      <button
        data-action-id="export.confirm"
        onClick={handleExport}
        className="w-full py-4 rounded-3xl text-[17px] font-bold tracking-wide bg-[var(--accent)] text-[var(--text-inverse)] shadow-[0_8px_20px_-6px_var(--accent-glow)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
      >
        {t("alertExportTranscriptOptions.exportButton")}
      </button>
    </ModalContainer>
  );
};

/* Toggle option row */
const ToggleOption = ({
  actionId,
  label,
  value,
  onChange,
}: {
  actionId: string;
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) => (
  <button
    data-action-id={actionId}
    onClick={() => onChange(!value)}
    className="glass-tile glass-raise w-full flex items-center justify-between p-5 rounded-3xl transition-all duration-200 active:scale-[0.98] group"
  >
    <span className="text-[15px] font-medium text-[var(--text-primary)]">
      {label}
    </span>
    <div
      className={`
        w-12 h-7 rounded-full relative transition-all duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]
        ${value ? "bg-[var(--accent)]" : "bg-[var(--glass-bg-active)]"}
      `}
    >
      <div
        className={`
          absolute top-0.5 w-6 h-6 rounded-full shadow-sm
          flex items-center justify-center
          transition-all duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]
          ${
            value
              ? "left-[22px] bg-[var(--text-inverse)]"
              : "left-0.5 bg-[var(--text-tertiary)]"
          }
        `}
      >
        {value && (
          <CheckIcon
            size={12}
            strokeWidth={3}
            className="text-[var(--accent)]"
          />
        )}
      </div>
    </div>
  </button>
);

export default AlertExportTranscriptOptions;
